import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Sterling overlays — SidePanel, Modal, ToastStack.
 *
 * - SidePanel slides from the right at 480 px in 200 ms (640 px wide variant).
 *   40 % scrim. Esc closes (if dirty, host should show a confirmation modal).
 *   ⌘S triggers save when `onSave` is provided and the panel is dirty.
 * - Modal is centred at 480 px. Reserved for confirmations only.
 * - ToastStack pins top-right; max 3 visible; older toasts collapse into a pill.
 *   Auto-dismiss: 4 s success/info, 6 s warning, never for danger.
 */
import { useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import { Icon } from './Icon.js';
import { Button } from './Button.js';
export function SidePanel({ open, title, width = 480, onClose, onSave, dirty, saveLabel = 'Save', footer, children, }) {
    // Esc closes
    useEffect(() => {
        if (!open)
            return;
        function onKey(e) { if (e.key === 'Escape')
            onClose(); }
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onClose]);
    // ⌘S / Ctrl+S triggers save when dirty
    useEffect(() => {
        if (!open || !onSave)
            return;
        function onKey(e) {
            if ((e.metaKey || e.ctrlKey) && e.key === 's' && dirty) {
                e.preventDefault();
                onSave();
            }
        }
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onSave, dirty]);
    return (_jsxs(_Fragment, { children: [_jsx("div", { className: clsx('sterling-scrim', open && 'open'), onClick: onClose }), _jsxs("aside", { className: clsx('sterling-side-panel', open && 'open'), style: { width }, "aria-hidden": !open, role: "dialog", "aria-modal": "true", "aria-label": title, children: [_jsxs("header", { className: "sterling-side-panel-header", children: [_jsx("h2", { className: "ty-panel-title", children: title }), _jsxs("div", { className: "sterling-side-panel-actions", children: [_jsx(Button, { variant: "tertiary", onClick: onClose, children: "Cancel" }), onSave && (_jsx(Button, { variant: "primary", onClick: onSave, disabled: !dirty, children: saveLabel }))] })] }), _jsx("div", { className: "sterling-side-panel-body", children: children }), footer && _jsx("footer", { className: "sterling-side-panel-footer", children: footer })] })] }));
}
export function Modal({ open, title, children, confirmLabel = 'Confirm', variant = 'default', typeToConfirm, onConfirm, onCancel, }) {
    const [typed, setTyped] = useState('');
    useEffect(() => { if (!open)
        setTyped(''); }, [open]);
    useEffect(() => {
        if (!open)
            return;
        function onKey(e) { if (e.key === 'Escape')
            onCancel(); }
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onCancel]);
    if (!open)
        return null;
    const canConfirm = !typeToConfirm || typed.trim() === typeToConfirm.trim();
    return (_jsxs(_Fragment, { children: [_jsx("div", { className: "sterling-scrim open", onClick: onCancel }), _jsxs("div", { className: "sterling-modal", role: "dialog", "aria-modal": "true", "aria-label": title, children: [_jsx("div", { className: "sterling-modal-title", children: title }), _jsx("div", { className: "sterling-modal-body", children: children }), typeToConfirm && (_jsx("input", { className: "sterling-input sterling-modal-input", placeholder: `Type "${typeToConfirm}" to confirm`, value: typed, onChange: e => setTyped(e.target.value), autoFocus: true })), _jsxs("div", { className: "sterling-modal-actions", children: [_jsx(Button, { variant: "tertiary", onClick: onCancel, children: "Cancel" }), _jsx(Button, { variant: variant === 'danger' ? 'destructive' : 'primary', onClick: onConfirm, disabled: !canConfirm, children: confirmLabel })] })] })] }));
}
function Toast({ toast, onDismiss }) {
    useEffect(() => {
        if (toast.variant === 'danger')
            return; // manual dismiss only
        const ms = toast.variant === 'warning' ? 6000 : 4000;
        const t = setTimeout(() => onDismiss(toast.id), ms);
        return () => clearTimeout(t);
    }, [toast.id, toast.variant, onDismiss]);
    // Each branch returns a string literal, so the expression already widens
    // to the union 'check-circle' | 'alert-triangle' | 'alert-circle' | 'info'.
    // (`as const` on a ternary isn't supported in TS 5.x.)
    const iconName = toast.variant === 'success' ? 'check-circle' :
        toast.variant === 'warning' ? 'alert-triangle' :
            toast.variant === 'danger' ? 'alert-circle' :
                'info';
    return (_jsxs("div", { className: `sterling-toast sterling-toast-${toast.variant}`, role: toast.variant === 'danger' ? 'alert' : 'status', "aria-live": toast.variant === 'danger' ? 'assertive' : 'polite', children: [_jsx(Icon, { name: iconName, size: 16, className: "sterling-toast-icon" }), _jsxs("div", { className: "sterling-toast-body", children: [_jsx("div", { className: "sterling-toast-title", children: toast.title }), toast.desc && _jsx("div", { className: "sterling-toast-desc", children: toast.desc })] }), toast.action ? (_jsx("button", { className: "sterling-toast-action", onClick: () => { toast.action.onClick?.(); onDismiss(toast.id); }, children: toast.action.label })) : (_jsx("button", { className: "sterling-iconbtn sm", onClick: () => onDismiss(toast.id), "aria-label": "Dismiss", title: "Dismiss", children: _jsx(Icon, { name: "x", size: 14 }) }))] }));
}
export function ToastStack({ toasts, onDismiss, max = 3 }) {
    const visible = toasts.slice(-max);
    const overflow = toasts.length - visible.length;
    return (_jsxs("div", { className: "sterling-toast-stack", children: [overflow > 0 && (_jsxs("div", { className: "sterling-toast-overflow", children: [overflow, " more ", overflow === 1 ? 'item' : 'items'] })), visible.map(t => _jsx(Toast, { toast: t, onDismiss: onDismiss }, t.id))] }));
}
/**
 * Simple in-memory toast controller. Apps may swap in their own (e.g. a
 * Zustand store, Redux slice) — `ToastStack` is the only piece that needs
 * to render the toast list.
 */
export function useToasts() {
    const [toasts, setToasts] = useState([]);
    const push = useCallback((t) => {
        const id = Math.random().toString(36).slice(2, 9);
        setToasts(arr => [...arr, { id, ...t }]);
        return id;
    }, []);
    const dismiss = useCallback((id) => {
        setToasts(arr => arr.filter(t => t.id !== id));
    }, []);
    return { toasts, push, dismiss };
}
//# sourceMappingURL=Overlays.js.map